// =============================================================================
// domain/excursionDiagnose.ts — the DIAGNOSE & CALIBRATE measures.
// -----------------------------------------------------------------------------
// Entry Edge asks whether the setup offers anything. Exit Skill asks whether you
// keep it. These two ask the question that decides what you actually change:
//
//   Loser asymmetry    were the losers an ENTRY fault or an EXIT fault?
//   R-multiple         how big is the opportunity, and how much of it do you take?
//
// ── 🚨 THE WHOLE POINT OF LOSER ASYMMETRY IS THAT IT SPLITS ONE WORD ──
//
// "My losers are bad" is two completely different problems with opposite fixes. A
// loser that went against you from the first tick is a TRIGGER problem — the
// context filter fired wrong, and no amount of exit coaching helps. A loser that
// drifted your way first and then reversed is an EXIT problem — the entry worked
// and you handed a live winner back. Told apart by the two logger timestamps: did
// the favourable extreme happen BEFORE the adverse one.
//
// ⚠ AND THERE IS A THIRD ANSWER THE NAIVE SPLIT MISSES. A setup whose losers
// drift 0.4R green and then fail is neither: it never produced a winner to give
// back. Calling that an exit problem sends someone to work on their exits when
// the setup has no follow-through. So a real "exit problem" needs BOTH a majority
// that went favourable first AND a real share that reached a full unit of risk.
//
// ── ⚠ EVERY MEASURE HERE IS SCOPED TO ONE INSTRUMENT BY THE CALLER ──
// Same rule as the two files next door: MAE and MFE are stored in POINTS.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// =============================================================================

import { median } from './excursionStats';
import { groupKeyOf, type EdgeTrade, type GroupBy, type NameLookup } from './excursionEdge';

// ---------------------------------------------------------------------------
// 1. Loser asymmetry — entry fault or exit fault
// ---------------------------------------------------------------------------

/** Fewer losers than this in a group and the split is noise. */
export const ASYMMETRY_MIN_LOSERS = 3;
/**
 * ⚠ POINTS. A loser has to have ticked at least this far your way to count as
 * having gone favourable at all — otherwise a single tick of noise on the wrong
 * side of the entry reads as "the entry worked".
 */
export const ASYMMETRY_FAVORABLE_MIN_POINTS = 0.5;
/** Below this share reaching a full unit of risk, it is not an exit problem. */
export const ASYMMETRY_GAVEBACK_MIN_PCT = 25;
/**
 * ⚠ ITS OWN FLOOR, not the display floor reused. A share of the R-measurable
 * losers is a different population from the row's losers — a group can show six
 * losers of which one carried a planned stop, and "100% gave back a winner" over
 * that one trade is the sentence that would send someone to rebuild their exits.
 */
export const ASYMMETRY_GAVEBACK_MIN_SAMPLE = 3;
/** A headline claim about a group wants more than the display floor. */
export const ASYMMETRY_HEADLINE_LOSERS = 5;

export type AsymmetryVerdict = 'entry' | 'exit' | 'no-follow-through' | 'mixed';

export interface AsymmetryRow {
  id: string;
  name: string;
  /** Losers carrying BOTH timings. The row's population. */
  losers: number;
  /** Went far enough your way BEFORE going against you. */
  favoredFirst: number;
  adverseFirst: number;
  favoredPct: number;
  /** Median favourable reach of a loser, in units of planned risk. */
  medMfeR: number | null;
  /**
   * Share of the R-measurable losers that reached a full unit of risk before
   * reversing — the honest exit-problem signal.
   *
   * ⚠ NULL, NOT ZERO, when no loser carries a planned stop. "None of them
   * reached 1R" and "none of them could be measured in R" are different facts
   * and only the first is an argument about the trader's exits.
   */
  gaveBackPct: number | null;
  /** ⚠ The population behind `gaveBackPct`. A share over 2 of 19 is not a share. */
  rSample: number;
  /** Median seconds to the favourable peak, over the favoured-first losers. */
  medMfeSecs: number | null;
  /** Median seconds to the adverse extreme, over ALL of the row's losers. */
  medMaeSecs: number | null;
  verdict: AsymmetryVerdict;
}

export interface LoserAsymmetry {
  rows: AsymmetryRow[];
  /**
   * ⚠ THE FOUR COUNTS ARE THE EMPTY STATE'S WHOLE VALUE. "No pattern met the
   * threshold" tells a trader nothing they can act on; "38 losers, 4 with logger
   * timing" tells them their older imports predate tick timing, and "31 with
   * timing, none also tagged" tells them to tag their trades.
   */
  totalLosers: number;
  withTiming: number;
  withGroup: number;
  withBoth: number;
  /** Highest give-back share among groups with a real sample. */
  mostExitBroken: AsymmetryRow | null;
  /** Lowest favourable share among the same. */
  mostEntryBroken: AsymmetryRow | null;
}

/**
 * Were the losers wrong from the first tick, or did you hand them back?
 *
 * ⚠ LOSERS ONLY, and the reason is not sample size. The winners already answer
 * their own question on the two Entry Edge cards; a loser is the only trade where
 * "was this an entry or an exit fault" has two live answers.
 *
 * ⚠ A LOSER WITH ONLY ONE TIMING IS EXCLUDED, never guessed at. The classifier
 * is a comparison between two timestamps — with one of them missing there is no
 * ordering to read, and defaulting it either way would silently push every
 * unlogged trade into one bucket.
 */
export function loserAsymmetry(
  trades: readonly EdgeTrade[], by: GroupBy, nameOf: NameLookup,
): LoserAsymmetry {
  interface Loser { maeSecs: number; mfeSecs: number; mfePoints: number; mfeR: number | null }
  const groups = new Map<string, { name: string; losers: Loser[] }>();
  let totalLosers = 0; let withTiming = 0; let withGroup = 0; let withBoth = 0;

  for (const t of trades) {
    if ((Number(t.netPnl) || 0) >= 0) continue;
    totalLosers += 1;

    // ⚠ `== null` FIRST. `Number(null)` is **0**, so `Number.isFinite(Number(x))`
    // is TRUE for a missing timing and every unlogged loser would be classified
    // as having peaked at second zero — which orders BEFORE any real adverse
    // extreme and would report the whole book as an exit problem.
    const hasTiming = t.maeSecs != null && t.mfeSecs != null
      && Number.isFinite(Number(t.maeSecs)) && Number.isFinite(Number(t.mfeSecs));
    const id = groupKeyOf(t, by);
    if (hasTiming) withTiming += 1;
    if (id) withGroup += 1;
    if (hasTiming && id) withBoth += 1;

    if (!id || !hasTiming) continue;
    const name = nameOf(id);
    if (name == null) continue;

    const mfePoints = Math.abs(Number(t.mfePoints) || 0);
    const risk = Number(t.riskPoints);
    const slot = groups.get(id) ?? { name: name || '(Untitled)', losers: [] };
    slot.losers.push({
      maeSecs: Number(t.maeSecs),
      mfeSecs: Number(t.mfeSecs),
      mfePoints,
      mfeR: Number.isFinite(risk) && risk > 0 ? mfePoints / risk : null,
    });
    groups.set(id, slot);
  }

  const rows: AsymmetryRow[] = [];
  for (const [id, g] of groups) {
    if (g.losers.length < ASYMMETRY_MIN_LOSERS) continue;

    const favored = g.losers.filter(
      (l) => l.mfePoints >= ASYMMETRY_FAVORABLE_MIN_POINTS && l.mfeSecs < l.maeSecs,
    );
    const favoredPct = (100 * favored.length) / g.losers.length;

    const rVals = g.losers.map((l) => l.mfeR).filter((r): r is number => r != null);
    const gaveBackPct = rVals.length
      ? (100 * rVals.filter((r) => r >= 1).length) / rVals.length : null;

    rows.push({
      id, name: g.name,
      losers: g.losers.length,
      favoredFirst: favored.length,
      adverseFirst: g.losers.length - favored.length,
      favoredPct,
      medMfeR: median(rVals),
      gaveBackPct,
      rSample: rVals.length,
      // ⚠ The favourable timing is meaningful only on the trades that WENT
      // favourable first. A loser that went straight against you still carries an
      // mfeSecs — of a tick that happened after the damage.
      medMfeSecs: median(favored.map((l) => l.mfeSecs)),
      medMaeSecs: median(g.losers.map((l) => l.maeSecs)),
      verdict: asymmetryVerdict(favoredPct, gaveBackPct, rVals.length),
    });
  }

  const sized = rows.filter((r) => r.losers >= ASYMMETRY_HEADLINE_LOSERS);
  const exits = sized.filter((r) => r.verdict === 'exit')
    .sort((a, b) => (b.gaveBackPct ?? 0) - (a.gaveBackPct ?? 0));
  const entries = sized.filter((r) => r.verdict === 'entry')
    .sort((a, b) => a.favoredPct - b.favoredPct);

  return {
    rows,
    totalLosers, withTiming, withGroup, withBoth,
    mostExitBroken: exits[0] ?? null,
    mostEntryBroken: entries[0] ?? null,
  };
}

/**
 * ⚠ THE ORDER IS THE MEANING, and "no follow-through" is the entry that makes it
 * honest. Without it, a setup whose losers drift 0.4R green and die is reported
 * as an exit problem — and the trader spends a month working on exits for a setup
 * that never handed them anything to keep.
 *
 * ⚠ The exit branch needs a REAL R population as well as a share. A single
 * measurable loser that happened to reach 1R is 100% and means nothing.
 */
export function asymmetryVerdict(
  favoredPct: number, gaveBackPct: number | null, rSample: number,
): AsymmetryVerdict {
  if (favoredPct <= 30) return 'entry';
  if (favoredPct >= 60) {
    const real = rSample >= ASYMMETRY_GAVEBACK_MIN_SAMPLE
      && gaveBackPct != null && gaveBackPct >= ASYMMETRY_GAVEBACK_MIN_PCT;
    return real ? 'exit' : 'no-follow-through';
  }
  return 'mixed';
}

// ---------------------------------------------------------------------------
// 2. R-multiple at MFE / MAE
// ---------------------------------------------------------------------------

/** Fewer winners than this and neither median is a median. */
export const RMULT_MIN_WINNERS = 5;
/** A headline claim wants more than the display floor. */
export const RMULT_HEADLINE_WINNERS = 10;
/** Above this, on a group with a real loser sample, the stop was not honoured. */
export const RMULT_STOP_BUST_R = 1.2;

export type CaptureVerdict = 'stops-not-honoured' | 'big-gap' | 'moderate-gap' | 'solid' | 'unknown';

/** What R-multiple needs beyond the Entry Edge shape. */
export interface DiagnoseTrade extends EdgeTrade {
  /**
   * Realised R as the trade recorded it.
   *
   * ⚠ READ, NOT RE-DERIVED from points ÷ risk. The stored figure is what every
   * other surface in the product prints, and a second derivation here would
   * disagree on any trade whose risk was edited after the fact — leaving two
   * different "your typical winner made 1.1R" claims on two tabs.
   */
  rrRealized: number | null;
}

export interface RMultipleRow {
  id: string;
  name: string;
  winners: number;
  losers: number;
  /** Median favourable excursion on winners, in units of planned risk. */
  medMfeR: number | null;
  /** Median realised R on the same winners that carried one. */
  medRealizedR: number | null;
  /** ⚠ Its own population — a winner with no stored R is in `winners`, not here. */
  realizedSample: number;
  /** Median adverse excursion on LOSERS, in R. Above 1 the stop was breached. */
  medLoserMaeR: number | null;
  /** realised ÷ offered. Null when either median is missing. */
  capture: number | null;
  verdict: CaptureVerdict;
}

export interface RMultipleProfile {
  rows: RMultipleRow[];
  /** Pooled across the rows shown, not across the whole book. */
  overallMfeR: number | null;
  overallRealizedR: number | null;
  overallCapture: number | null;
  /** ⚠ The two pools differ whenever a winner has no stored realised R. */
  mfeSample: number;
  realizedSample: number;
  /** Largest capture gap among groups with a real winner sample. */
  worstCapture: RMultipleRow | null;
  /** Worst breached stop among groups with a real loser sample. */
  worstStops: RMultipleRow | null;
}

/**
 * How big the opportunity is, and how much of it you take.
 *
 * ⚠ NORMALISED TO RISK, which is what makes this the one excursion card that
 * compares across instruments and stop sizes — 2.8R on MES and 2.8R on CL mean
 * the same thing where 2.8 points do not. The caller still scopes by instrument
 * (everything else on the tab is in points) but this measure would survive
 * without it.
 *
 * ⚠ A TRADE WITH NO PLANNED RISK IS EXCLUDED ENTIRELY. Dividing by a missing
 * stop is the whole failure mode this card is supposed to expose, and a fallback
 * risk would manufacture an R for a trade that never had one.
 */
export function rMultipleProfile(
  trades: readonly DiagnoseTrade[], by: GroupBy, nameOf: NameLookup,
): RMultipleProfile {
  const groups = new Map<string, {
    name: string; mfeR: number[]; realizedR: number[]; loserMaeR: number[];
    winners: number; losers: number;
  }>();

  for (const t of trades) {
    const id = groupKeyOf(t, by);
    if (!id) continue;
    const name = nameOf(id);
    if (name == null) continue;

    const risk = Number(t.riskPoints);
    if (!Number.isFinite(risk) || risk <= 0) continue;
    // ⚠ BOTH excursions required, even though a winner's row only reads MFE. A
    // group whose winners were measured and whose losers were not would report a
    // clean loser MAE-R that describes a handful of trades.
    if (t.mfePoints == null || t.maePoints == null) continue;
    const mfe = Number(t.mfePoints); const mae = Number(t.maePoints);
    if (!Number.isFinite(mfe) || !Number.isFinite(mae)) continue;

    const pnl = Number(t.netPnl) || 0;
    if (pnl === 0) continue;   // a scratch is neither an opportunity nor a stop test

    const g = groups.get(id) ?? {
      name: name || '(Untitled)', mfeR: [], realizedR: [], loserMaeR: [], winners: 0, losers: 0,
    };
    if (pnl > 0) {
      g.winners += 1;
      g.mfeR.push(Math.abs(mfe) / risk);
      // ⚠ `== null` first, again. `Number(null)` is 0 — a winner with no stored R
      // would enter the realised pool as a scratch and drag the median down,
      // which reads as a capture problem that is really a missing field.
      if (t.rrRealized != null && Number.isFinite(Number(t.rrRealized))) {
        g.realizedR.push(Number(t.rrRealized));
      }
    } else {
      g.losers += 1;
      g.loserMaeR.push(Math.abs(mae) / risk);
    }
    groups.set(id, g);
  }

  const rows: RMultipleRow[] = [];
  const pooledMfe: number[] = [];
  const pooledRealized: number[] = [];

  for (const [id, g] of groups) {
    if (g.winners < RMULT_MIN_WINNERS) continue;
    const medMfeR = median(g.mfeR);
    const medRealizedR = median(g.realizedR);
    const medLoserMaeR = median(g.loserMaeR);
    const capture = (medMfeR != null && medMfeR > 0 && medRealizedR != null)
      ? medRealizedR / medMfeR : null;

    pooledMfe.push(...g.mfeR);
    pooledRealized.push(...g.realizedR);

    rows.push({
      id, name: g.name, winners: g.winners, losers: g.losers,
      medMfeR, medRealizedR, realizedSample: g.realizedR.length,
      medLoserMaeR, capture,
      verdict: captureVerdict(capture, medLoserMaeR),
    });
  }

  const overallMfeR = median(pooledMfe);
  const overallRealizedR = median(pooledRealized);

  const sized = rows.filter((r) => r.winners >= RMULT_HEADLINE_WINNERS && r.capture != null);
  const busted = rows.filter(
    (r) => r.medLoserMaeR != null && r.medLoserMaeR > RMULT_STOP_BUST_R
      && r.losers >= RMULT_MIN_WINNERS,
  );

  return {
    rows,
    overallMfeR, overallRealizedR,
    overallCapture: (overallMfeR != null && overallMfeR > 0 && overallRealizedR != null)
      ? overallRealizedR / overallMfeR : null,
    mfeSample: pooledMfe.length,
    realizedSample: pooledRealized.length,
    worstCapture: sized.length
      ? [...sized].sort((a, b) => (a.capture as number) - (b.capture as number))[0] ?? null
      : null,
    worstStops: busted.length
      ? [...busted].sort((a, b) => (b.medLoserMaeR as number) - (a.medLoserMaeR as number))[0] ?? null
      : null,
  };
}

/**
 * ⚠ THE BREACHED STOP OUTRANKS THE CAPTURE GAP, deliberately.
 *
 * A group whose losers ran past their own stop is not an exit-timing problem to
 * be tuned — it is a discipline problem, and every other number on the row is
 * computed against a risk the trader did not actually take. Reporting "capture
 * solid" on top of it would be true and useless.
 */
export function captureVerdict(
  capture: number | null, medLoserMaeR: number | null,
): CaptureVerdict {
  if (medLoserMaeR != null && medLoserMaeR > 1.3) return 'stops-not-honoured';
  if (capture == null) return 'unknown';
  if (capture < 0.4) return 'big-gap';
  if (capture < 0.6) return 'moderate-gap';
  return 'solid';
}
